import { useState, useEffect } from "react";
import { useAdminApi, useClasses } from "../adminUtils";
import SignatureSelect from "./SignatureSelect";
import "../admin-base.css";
import "../admin-layout.css";
import "../admin-controls.css";
import "../admin-students.css";
import "../admin-misc.css";
import "../admin-responsive.css";

const EMPTY_FORM = {
  audienceType: "individual",
  studentEmail: "",
  classId: "",
  date: "",
  subject: "",
  body: "",
  signatureId: "default",
};

export default function MessagesTab() {
  const { API, AUTH, IS_OWNER } = useAdminApi();
  const classes = useClasses();

  const [form, setForm]                     = useState(EMPTY_FORM);
  const [recipientCount, setRecipientCount] = useState(null);
  const [countLoading, setCountLoading]     = useState(false);
  const [sending, setSending]               = useState(false);
  const [error, setError]                   = useState(null);
  const [result, setResult]                 = useState(null);

  useEffect(() => {
    setRecipientCount(null);
    setError(null);

    if (form.audienceType === "all") {
      setCountLoading(true);
      fetch(`${API}/messages/all-count`, { headers: AUTH })
        .then(res => res.json())
        .then(data => setRecipientCount(data.count ?? 0))
        .catch(() => setRecipientCount(null))
        .finally(() => setCountLoading(false));
    } else if (form.audienceType === "roster" && form.classId && form.date) {
      setCountLoading(true);
      fetch(`${API}/messages/roster-preview?classId=${form.classId}&date=${form.date}`, { headers: AUTH })
        .then(res => res.json())
        .then(data => setRecipientCount(data.count ?? 0))
        .catch(() => setRecipientCount(null))
        .finally(() => setCountLoading(false));
    } else if (form.audienceType === "individual" && form.studentEmail.includes("@")) {
      setRecipientCount(1);
    }
  }, [form.audienceType, form.classId, form.date, form.studentEmail, API, AUTH]);

  const buildAudience = () => {
    if (form.audienceType === "individual") return { type: "individual", studentEmail: form.studentEmail.trim() };
    if (form.audienceType === "roster")     return { type: "roster", classId: form.classId, date: form.date };
    return { type: "all" };
  };

  const handleSend = async () => {
    setError(null); setResult(null);

    if (!form.subject.trim()) { setError("Subject is required."); return; }
    if (!form.body.trim()) { setError("Message is required."); return; }
    if (form.audienceType === "individual" && !form.studentEmail.trim()) { setError("Enter a recipient email."); return; }
    if (form.audienceType === "roster" && (!form.classId || !form.date)) { setError("Choose a class and date."); return; }

    const confirmMsg = form.audienceType === "all"
      ? `Send this email to all ${recipientCount ?? "?"} recipients? This cannot be undone.`
      : form.audienceType === "roster"
      ? `Send this email to the ${recipientCount ?? "?"} recipient(s) booked into this class? This cannot be undone.`
      : `Send this email?`;
    if (!window.confirm(confirmMsg)) return;

    setSending(true);
    try {
      const payload = { audience: buildAudience(), subject: form.subject.trim(), body: form.body.trim(), signatureId: form.signatureId };
      const res = await fetch(`${API}/messages/email`, {
        method: "POST", headers: AUTH,
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? "Failed to send."); return; }
      setResult(data);
      setForm(f => ({ ...EMPTY_FORM, audienceType: f.audienceType }));
    } catch {
      setError("Something went wrong.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="adm-tab-content">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
        <h2 style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--color-grey-600)" }}>Send a Message</h2>
      </div>

      <div className="adm-settings-section" style={{ marginBottom: "1.5rem" }}>
        <h3 className="adm-settings-heading">Recipients</h3>
        <div style={{ display: "flex", gap: "1.5rem", marginBottom: "1rem" }}>
          {[
            { value: "individual", label: "Individual" },
            { value: "roster",     label: "Class Roster" },
            { value: "all",        label: "All Students" },
          ].filter(opt => IS_OWNER || opt.value !== "all").map(opt => (
            <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.9rem", cursor: "pointer" }}>
              <input type="radio" name="audienceType" value={opt.value}
                checked={form.audienceType === opt.value}
                onChange={e => setForm(f => ({ ...f, audienceType: e.target.value }))} />
              {opt.label}
            </label>
          ))}
        </div>

        {form.audienceType === "individual" && (
          <label className="adm-form-label" style={{ maxWidth: "360px" }}>
            Recipient email
            <input className="adm-input" type="email" value={form.studentEmail}
              onChange={e => setForm(f => ({ ...f, studentEmail: e.target.value }))}
              placeholder="student@email.com" />
          </label>
        )}

        {form.audienceType === "roster" && (
          <div className="adm-form-grid" style={{ maxWidth: "480px" }}>
            <label className="adm-form-label">Class
              <select className="adm-input" value={form.classId}
                onChange={e => setForm(f => ({ ...f, classId: e.target.value }))}>
                <option value="">Choose a class…</option>
                {classes.map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </label>
            <label className="adm-form-label">Date
              <input className="adm-input" type="date" value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </label>
          </div>
        )}

        {(recipientCount !== null || countLoading) && (
          <p style={{ marginTop: "0.75rem", fontSize: "0.85rem", color: "var(--color-grey-400)" }}>
            {countLoading ? "Checking recipients…" : `${recipientCount} recipient${recipientCount === 1 ? "" : "s"}`}
          </p>
        )}
      </div>

      <div className="adm-settings-section" style={{ marginBottom: "1.5rem" }}>
        <h3 className="adm-settings-heading">Message</h3>
        <div className="adm-form-grid" style={{ maxWidth: "640px" }}>
          <label className="adm-form-label" style={{ gridColumn: "1/-1" }}>Subject
            <input className="adm-input" value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              placeholder="e.g. Studio closed this Friday" />
          </label>
          <label className="adm-form-label" style={{ gridColumn: "1/-1" }}>Message
            <textarea className="adm-input" rows={8} value={form.body}
              onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
              placeholder="Write your message here. Blank lines separate paragraphs." />
          </label>
          <label className="adm-form-label" style={{ gridColumn: "1/-1" }}>Signature
            <SignatureSelect value={form.signatureId} onChange={v => setForm(f => ({ ...f, signatureId: v }))} />
          </label>
        </div>
      </div>

      {error && <p className="adm-error" style={{ marginBottom: "1rem" }}>{error}</p>}
      {result && (
        <p style={{ marginBottom: "1rem", fontSize: "0.9rem", color: "var(--color-primary-variant)" }}>
          ✓ Sent to {result.sent} of {result.total} recipient{result.total === 1 ? "" : "s"}.
          {result.failed?.length > 0 && ` ${result.failed.length} failed.`}
        </p>
      )}

      <button className="adm-btn--primary" onClick={handleSend} disabled={sending}>
        {sending ? "Sending…" : "Send Message"}
      </button>
    </div>
  );
}
